
import React from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ChevronLeft, AlertTriangle, RefreshCw } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Navbar from "@/components/Navbar";
import BackupTable from "@/components/backup/BackupTable";
import EmptyBackupState from "@/components/backup/EmptyBackupState";
import { useBackupHistory } from "@/hooks/useBackupHistory";
import { Toaster } from "@/components/ui/toaster";

export default function BackupHistory() {
  // Make sure we're getting the actual websiteId string value
  const { websiteId } = useParams<{ websiteId: string }>();
  
  const {
    backups,
    isLoading,
    error,
    websiteTitle,
    deleteInProgress,
    handleDeleteBackup,
    formatDate,
    refreshBackups,
  } = useBackupHistory(websiteId);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isLoggedIn={true} />
      <Toaster />
      
      <div className="container mx-auto py-10 px-4 max-w-6xl">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <Button variant="outline" asChild className="mb-4">
              <Link to="/dashboard">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
            
            <h1 className="text-3xl font-bold mb-2">{websiteTitle}</h1>
            <p className="text-gray-500">
              View, restore, or delete backups for your website.
            </p>
          </div>
          
          <Button variant="outline" onClick={refreshBackups} className="flex gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <p className="text-sm text-gray-500">Loading backup history...</p>
            </div>
          </div>
        ) : backups.length > 0 ? (
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <BackupTable
                  backups={backups}
                  websiteId={websiteId || ""}
                  deleteInProgress={deleteInProgress}
                  onDeleteBackup={handleDeleteBackup}
                  formatDate={formatDate}
                />
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {backups.length} backups
              </div>
              
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious href="#" />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href="#" isActive>1</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext href="#" />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        ) : (
          <EmptyBackupState />
        )}
      </div>
    </div>
  );
}
